/**
 * Auth store — single-user JWT, hydrated from localStorage.
 *
 * The token is issued by Compass itself (compass-postgres holds the
 * password_hash, compass mints the JWT with COMPASS_JWT_SECRET). Auth
 * is independent of paddock — trades survive viki01 being down.
 */
import { create } from 'zustand'

const TOKEN_KEY = 'compass.auth.token'
const EXPIRES_KEY = 'compass.auth.expires'
const USERNAME_KEY = 'compass.auth.username'

interface AuthState {
  token: string | null
  username: string | null
  expiresAt: string | null  // ISO
  hydrated: boolean
  setAuth: (token: string, expiresAt: string, username: string) => void
  clearAuth: () => void
  hydrate: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  username: null,
  expiresAt: null,
  hydrated: false,

  setAuth: (token, expiresAt, username) => {
    try {
      localStorage.setItem(TOKEN_KEY, token)
      localStorage.setItem(EXPIRES_KEY, expiresAt)
      localStorage.setItem(USERNAME_KEY, username)
    } catch {/* ignore */}
    set({ token, expiresAt, username, hydrated: true })
  },

  clearAuth: () => {
    try {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(EXPIRES_KEY)
      localStorage.removeItem(USERNAME_KEY)
    } catch {/* ignore */}
    set({ token: null, expiresAt: null, username: null, hydrated: true })
  },

  hydrate: () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY)
      const expiresAt = localStorage.getItem(EXPIRES_KEY)
      const username = localStorage.getItem(USERNAME_KEY)
      if (token && expiresAt) {
        // Check expiry client-side as a hint — server will reject hard-expired tokens too.
        if (new Date(expiresAt).getTime() > Date.now()) {
          set({ token, expiresAt, username, hydrated: true })
          return
        }
      }
    } catch {/* ignore */}
    set({ hydrated: true })
  },
}))
